/**
 * Read EVERY row a query matches, whatever the server's row cap happens to be.
 *
 * PostgREST truncates a response at its `max-rows` setting. That cap is a
 * server setting this code cannot read, so nothing here may assume a value for
 * it — including "it is at least as large as the page we ask for". The loop
 * therefore advances by what actually came BACK and stops only on an empty
 * page. Advancing by the requested size instead, and stopping on a short page,
 * silently truncates on any project whose cap is below `PAGE`: the very first
 * response is short and the list simply ends there — which is exactly the bug
 * paging was added to prevent.
 *
 * `page` must apply a stable total ordering, or rows can repeat or vanish
 * between requests.
 */
export async function fetchAllRows<T>(
  label: string,
  page: (
    from: number,
    size: number
  ) => PromiseLike<{ data: T[] | null; error: unknown }>
): Promise<T[]> {
  const PAGE = 1000;
  // Pathological guard only (a server that keeps handing back rows). Throwing
  // beats returning a short list: a silently incomplete result is the failure
  // mode this exists to avoid, and every caller already handles the error.
  const MAX_REQUESTS = 500;
  const all: T[] = [];
  let from = 0;
  for (let request = 0; ; request++) {
    if (request >= MAX_REQUESTS) {
      throw new Error(`${label}: paging did not terminate`);
    }
    const { data, error } = await page(from, PAGE);
    if (error) throw error;
    const received = data ?? [];
    all.push(...received);
    if (received.length === 0) break;
    from += received.length;
  }
  return all;
}
